import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Spinner } from '@/components/ui';
import { type ImportResult, type ImportRowError } from '@/hooks/useAdminImports';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Generic Excel/CSV import modal. Used by both /admin/rates and
// /admin/plans. Flow:
//
//   1. operator picks a file
//   2. preview runs server-side; we render headers + per-row errors
//   3. operator confirms → import runs; we render counts + remaining errors
//
// `previewSlot` lets each importer render its own sample table since the
// columns differ (rates vs plans). Everything else is shared.
// ---------------------------------------------------------------------------

interface PreviewSummary {
  detected_kind: string;
  headers: string[];
  total_rows: number;
  valid_rows: number;
  errors: ImportRowError[];
}

export interface ImportModalProps {
  /** Modal title and subtitle (i18n-resolved by the caller). */
  title: string;
  subtitle: string;
  /** Localized note explaining required columns to the operator. */
  schemaHint: string;
  /** Lifecycle callbacks; the parent owns the open/close state. */
  onClose: () => void;
  /** Returns the preview payload. Throws on parser/validation error. */
  onPreview: (file: File) => Promise<PreviewSummary>;
  /** Returns the import result. Throws on transport/HTTP error. */
  onImport: (file: File) => Promise<ImportResult>;
  /** Per-importer table rendering the parsed sample rows. */
  previewSlot?: JSX.Element | null;
}

export function ImportModal({
  title,
  subtitle,
  schemaHint,
  onClose,
  onPreview,
  onImport,
  previewSlot,
}: ImportModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewSummary | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'import' | null>(null);

  const reset = (): void => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const onFileChange = async (next: File | null): Promise<void> => {
    setFile(next);
    setPreview(null);
    setResult(null);
    setError(null);
    if (!next) return;
    setBusy('preview');
    try {
      const p = await onPreview(next);
      setPreview(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('imp_err_generic'));
    } finally {
      setBusy(null);
    }
  };

  const runImport = async (): Promise<void> => {
    if (!file) return;
    setBusy('import');
    setError(null);
    try {
      const r = await onImport(file);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('imp_err_generic'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[720px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">{title}</h2>
          <p className="text-[12px] text-text-3 mt-0.5">{subtitle}</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {!result && (
            <div>
              <label className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('imp_file_label')}
              </label>
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv,text/csv"
                onChange={(e) => void onFileChange(e.target.files?.[0] ?? null)}
                disabled={busy !== null}
                className="block w-full text-[13px] text-text file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-line file:bg-surface-2 file:text-text-2 file:cursor-pointer hover:file:bg-surface-3"
              />
              <p className="text-[11.5px] text-text-4 mt-1.5">{schemaHint}</p>
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {error}
            </div>
          )}

          {busy === 'preview' && (
            <div className="flex items-center gap-2 text-[13px] text-text-3">
              <Spinner />
              <span>{t('imp_parsing')}</span>
            </div>
          )}

          {preview && !result && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-[12px]">
                <Badge variant="neutral">{preview.detected_kind.toUpperCase()}</Badge>
                <span className="text-text-2">
                  {t('imp_summary', {
                    total: preview.total_rows,
                    valid: preview.valid_rows,
                    invalid: preview.total_rows - preview.valid_rows,
                  })}
                </span>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium mb-1.5">
                  {t('imp_headers')}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {preview.headers.map((h) => (
                    <span
                      key={h}
                      className="mono text-[11px] px-1.5 py-0.5 bg-surface-3 rounded-sm text-text-3"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>

              {previewSlot && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium mb-1.5">
                    {t('imp_sample')}
                  </div>
                  {previewSlot}
                </div>
              )}

              {preview.errors.length > 0 && (
                <ErrorsTable errors={preview.errors} />
              )}
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <div className="px-3 py-3 bg-success-bg border border-success-bd rounded-md text-[13px] text-success">
                {t('imp_done', {
                  inserted: result.inserted,
                  updated: result.updated,
                  skipped: result.skipped,
                })}
              </div>
              {result.errors.length > 0 && <ErrorsTable errors={result.errors} />}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          {result ? (
            <Button onClick={onClose}>{t('imp_close')}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={busy !== null}>
                {t('imp_cancel')}
              </Button>
              {preview && preview.valid_rows > 0 && (
                <>
                  <Button variant="secondary" onClick={reset} disabled={busy !== null}>
                    {t('imp_pick_other')}
                  </Button>
                  <Button onClick={() => void runImport()} disabled={busy !== null}>
                    {busy === 'import' ? (
                      <>
                        <Spinner />
                        <span>{t('imp_importing')}</span>
                      </>
                    ) : (
                      t('imp_run', { count: preview.valid_rows })
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrorsTable({ errors }: { errors: ImportRowError[] }): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium mb-1.5">
        {t('imp_errors', { count: errors.length })}
      </div>
      <div className="border border-line rounded-md overflow-hidden max-h-[180px] overflow-y-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[11px] text-text-4 uppercase tracking-[0.06em]">
              <th className="px-3 py-1.5 font-medium w-16">{t('imp_err_col_row')}</th>
              <th className="px-3 py-1.5 font-medium">{t('imp_err_col_msg')}</th>
            </tr>
          </thead>
          <tbody>
            {errors.slice(0, 50).map((e, i) => (
              <tr key={i} className={cn('border-t border-line-soft')}>
                <td className="px-3 py-1.5 num text-text-3">{e.row}</td>
                <td className="px-3 py-1.5 text-danger">{e.message}</td>
              </tr>
            ))}
            {errors.length > 50 && (
              <tr className="border-t border-line-soft">
                <td colSpan={2} className="px-3 py-2 text-center text-[11.5px] text-text-3">
                  {t('imp_err_more', { count: errors.length - 50 })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
